import fm from 'front-matter'

/**
 * The read side of VERIFY — the content-validation layer that records what has
 * been checked, against what source, by whom, and when. **Fact Check** is what
 * that layer is called on screen; the vault's own schema keeps the older
 * `verification:` spelling, so this module reads one and speaks the other.
 *
 * The vault is the source of truth. Every content file carries a `verification:`
 * block in its frontmatter (written and policed by `scripts/verify_check.py`);
 * this module parses that block, and the append-only sidecar log that backs it,
 * for the Fact Check surfaces that show a student what has been checked.
 *
 * Two things worth keeping straight:
 *
 *  - The block is *bound to the bytes of the file*, not to its name. A file
 *    edited after being verified is downgraded to `stale` by tooling, so a green
 *    badge always describes the text actually on screen.
 *  - Sidecar logs are deliberately **not** bundled at build time. They grow
 *    without bound and only matter when a reader opens the log panel, so
 *    `fetchVerificationLog` pulls one on demand. Everything the badge needs —
 *    status, date, open-finding count — is in the block itself.
 */

export type VerificationStatus =
  | 'unverified'
  | 'in_review'
  | 'verified'
  | 'disputed'
  | 'stale'

export type VerificationConfidence = 'high' | 'medium' | 'low'

export interface Verification {
  status: VerificationStatus
  confidence: VerificationConfidence | null
  /** ISO `YYYY-MM-DD`, or null when the page has never been checked. */
  lastChecked: string | null
  /** `agent:validate-v1` or `human:jordan`, or null when never checked. */
  lastCheckedBy: string | null
  contentHash: string
  sources: string[]
  openFindings: number
  /**
   * How many of those are `critical`. Carried in the block rather than derived
   * from the log because sidecar logs are not bundled at build time, and the
   * quiz needs severity to decide what to exclude from a session.
   */
  openCritical: number
  /** Repo-relative path of the sidecar log, e.g. `.verify/Concepts/Convexity.md`. */
  log: string
}

const STATUSES: VerificationStatus[] = [
  'unverified', 'in_review', 'verified', 'disputed', 'stale',
]
const CONFIDENCES: VerificationConfidence[] = ['high', 'medium', 'low']

/** The block every page falls back to: nothing claimed, nothing checked. */
export const UNVERIFIED: Verification = {
  status: 'unverified',
  confidence: null,
  lastChecked: null,
  lastCheckedBy: null,
  contentHash: '',
  sources: [],
  openFindings: 0,
  openCritical: 0,
  log: '',
}

function asString(value: unknown): string | null {
  if (value == null) return null
  // js-yaml turns an unquoted `2026-08-23` into a Date; normalise back to ISO.
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10)
  const text = String(value).trim()
  return text === '' ? null : text
}

/**
 * Read the `verification:` block out of already-parsed frontmatter attributes.
 * Unknown or malformed values degrade to the `unverified` default rather than
 * throwing — a page must still render when its block is wrong; CI is what fails
 * on a malformed block, not the reader.
 */
export function verificationFromAttributes(attrs: unknown): Verification | null {
  if (attrs == null || typeof attrs !== 'object') return null
  const raw = (attrs as Record<string, unknown>).verification
  if (raw == null || typeof raw !== 'object') return null
  const block = raw as Record<string, unknown>

  const status = asString(block.status)
  const confidence = asString(block.confidence)
  const openFindings = Number(block.open_findings)
  const openCritical = Number(block.open_critical)

  return {
    status: STATUSES.includes(status as VerificationStatus)
      ? (status as VerificationStatus)
      : 'unverified',
    confidence: CONFIDENCES.includes(confidence as VerificationConfidence)
      ? (confidence as VerificationConfidence)
      : null,
    lastChecked: asString(block.last_checked),
    lastCheckedBy: asString(block.last_checked_by),
    contentHash: asString(block.content_hash) ?? '',
    sources: Array.isArray(block.sources)
      ? block.sources.map((s) => String(s).trim()).filter(Boolean)
      : [],
    openFindings: Number.isFinite(openFindings) && openFindings > 0 ? Math.floor(openFindings) : 0,
    openCritical: Number.isFinite(openCritical) && openCritical > 0 ? Math.floor(openCritical) : 0,
    log: asString(block.log) ?? '',
  }
}

/** Parse the block straight out of a markdown file's raw text. */
export function parseVerification(markdown: string): Verification | null {
  try {
    return verificationFromAttributes(fm<Record<string, unknown>>(markdown).attributes)
  } catch {
    return null
  }
}

/** Where a content file's sidecar log lives. Mirrors `verify_lib.log_path_for`. */
export function verificationLogPath(contentPath: string): string {
  return `.verify/${contentPath.replace(/^\/+/, '')}`
}

/**
 * Recover a page's own vault path from its block.
 *
 * Questions reach the app as raw markdown with no filename attached — the build
 * collects file *contents*, and a question's `id` doesn't map to its filename
 * (`cas5-2013f-q1` lives in `cas5-2013f-001.md`). But `log:` is that path with
 * `.verify/` on the front, and CI enforces that it is correct, so the block
 * carries the answer already.
 */
export function contentPathFromVerification(v: Verification | null | undefined): string | null {
  if (!v?.log?.startsWith('.verify/')) return null
  const path = v.log.slice('.verify/'.length)
  return path || null
}

// ─── Badge presentation ──────────────────────────────────────────────────────

export type FactCheckTone = 'green' | 'amber' | 'grey' | 'red'

/**
 * Which verdict a badge is, finer than its tone: amber is both a page checked
 * with something still open and a page edited since its check, and the two
 * want different marks — a reader shown the "re-check" arrows on a page that
 * *was* checked reasonably reads it as not checked at all.
 */
export type FactCheckKind =
  | 'checked' | 'checked-open' | 'unchecked' | 'stale' | 'review' | 'issue' | 'disputed'

export interface FactCheckBadge {
  kind: FactCheckKind
  label: string
  /** The same verdict in one or two words, for a dense row or a menu pill. */
  short: string
  tone: FactCheckTone
  /** One sentence explaining what the badge actually promises. */
  detail: string
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `2026-08-12` → `12 Aug 2026`. Parsed by hand: `new Date('2026-08-12')` is UTC. */
export function formatCheckedDate(iso: string | null): string | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return null
  const month = MONTHS[Number(m[2]) - 1]
  if (!month) return null
  return `${Number(m[3])} ${month} ${m[1]}`
}

/**
 * What to show a student — the verdict the **Fact Check** surfaces read out.
 * Deliberately conservative: only a page whose status is `verified` *and* whose
 * hash still matches gets the green badge, and a page carrying an open finding
 * says so even when it was verified, because an open finding is exactly the
 * thing a reader needs to know about.
 */
export function factCheckBadge(v: Verification | null | undefined): FactCheckBadge {
  if (v && v.openCritical > 0 && v.status !== 'disputed') {
    // A critical finding outranks every other state: it is the one thing a
    // reader has to know before they trust the page.
    return {
      kind: 'issue',
      label: 'Known issue',
      short: 'Issue',
      tone: 'red',
      detail: `${v.openCritical} unresolved critical finding${v.openCritical === 1 ? '' : 's'} on this page.`,
    }
  }
  if (!v || v.status === 'unverified') {
    return {
      kind: 'unchecked',
      label: 'Not fact checked',
      short: 'Unchecked',
      tone: 'grey',
      detail: 'Not yet checked against a source.',
    }
  }
  if (v.status === 'disputed') {
    return {
      kind: 'disputed',
      label: 'Disputed',
      short: 'Disputed',
      tone: 'red',
      detail: 'Sources disagree, or something critical is unresolved.',
    }
  }
  if (v.status === 'stale') {
    return {
      kind: 'stale',
      label: 'Re-check needed',
      short: 'Re-check',
      tone: 'amber',
      detail: 'Edited since it was last checked.',
    }
  }
  if (v.status === 'in_review') {
    return {
      kind: 'review',
      label: 'Under review',
      short: 'Review',
      tone: 'amber',
      detail: 'A fact check is in progress.',
    }
  }
  const date = formatCheckedDate(v.lastChecked)
  if (v.openCritical > 0) {
    return {
      kind: 'issue',
      label: 'Known issue',
      short: 'Issue',
      tone: 'red',
      detail: `${v.openCritical} unresolved critical finding${v.openCritical === 1 ? '' : 's'} on this page.`,
    }
  }
  if (v.openFindings > 0) {
    return {
      kind: 'checked-open',
      label: date ? `Fact checked · ${date}` : 'Fact checked',
      short: 'Checked',
      tone: 'amber',
      detail: `Checked against ${v.sources.length} source${v.sources.length === 1 ? '' : 's'}, with ${v.openFindings} open finding${v.openFindings === 1 ? '' : 's'}.`,
    }
  }
  return {
    kind: 'checked',
    label: date ? `Fact checked · ${date}` : 'Fact checked',
    short: 'Checked',
    tone: 'green',
    detail: `Checked against ${v.sources.length} source${v.sources.length === 1 ? '' : 's'}.`,
  }
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export interface FactCheckOverview {
  /** The verdict, without the date — the sentence under it carries that. */
  headline: string
  /** One plain sentence: what was checked, when, and what is left. */
  sentence: string
}

/**
 * The top of the Fact Check panel: a verdict and one sentence that answers the
 * three things a reader looks for in it — *was it checked*, *when, against
 * how much*, and *is anything wrong*. The badge's label packs the date into the
 * verdict for a title row; here they are said in order instead, and an open
 * finding is named as such in the headline rather than left to a tint.
 */
export function factCheckOverview(v: Verification | null | undefined): FactCheckOverview {
  const badge = factCheckBadge(v)
  const date = formatCheckedDate(v?.lastChecked ?? null)
  const sources = v?.sources.length ?? 0
  const checkedOn = [
    sources > 0 ? `Checked against ${plural(sources, 'source')}` : 'Checked',
    date ? ` on ${date}` : '',
  ].join('')
  switch (badge.kind) {
    case 'unchecked':
      return { headline: badge.label, sentence: 'No one has checked this page against a source yet.' }
    case 'review':
      return { headline: badge.label, sentence: 'A fact check is under way.' }
    case 'stale':
      return {
        headline: badge.label,
        sentence: date
          ? `Edited since its last check on ${date}, so what is below describes the earlier version.`
          : 'Edited since its last check, so what is below describes the earlier version.',
      }
    case 'disputed':
      return { headline: badge.label, sentence: 'Sources disagree, or a critical issue is unresolved.' }
    case 'issue':
      return {
        headline: badge.label,
        sentence: `${plural(v?.openCritical ?? 0, 'critical issue')} open. Read ${(v?.openCritical ?? 0) === 1 ? 'it' : 'them'} before relying on this page.`,
      }
    case 'checked-open': {
      const n = v?.openFindings ?? 0
      return {
        headline: `Fact checked · ${plural(n, 'open issue')}`,
        sentence: `${checkedOn}. ${n === 1 ? 'The open issue is not' : 'None of the open issues is'} critical.`,
      }
    }
    case 'checked':
      return { headline: 'Fact checked', sentence: `${checkedOn}. No open issues.` }
  }
}

/** `agent:validate-v1` → "Automated check"; `human:jordan` → "jordan". */
export function checkerName(author: string): string {
  if (/^agent:/.test(author)) return 'Automated check'
  return author.replace(/^human:/, '')
}

// ─── Sidecar logs ────────────────────────────────────────────────────────────

export type LogEntryType = 'finding' | 'correction' | 'comment' | 'question' | 'resolution'
export type LogEntrySeverity = 'critical' | 'major' | 'minor' | 'nit'
export type LogEntryStatus = 'open' | 'resolved' | 'wontfix' | 'superseded'

export interface LogEntry {
  id: string
  title: string
  entryType: LogEntryType | ''
  author: string
  date: string
  severity: LogEntrySeverity | ''
  status: LogEntryStatus | ''
  resolves: string
  /**
   * Whether the correction this finding proposed has already been made to the
   * page. Independent of `status`: a fix can land before a human signs the
   * finding off, and the Fact Check panel marks such a row so a reader can see
   * that the text in front of them has already been changed.
   */
  applied: boolean
  /** Every `- key: value` field, in source order, for display. */
  fields: Array<{ key: string; value: string }>
}

export interface VerificationLog {
  target: string
  created: string
  entries: LogEntry[]
}

const ENTRY_HEADING = /^##\s+\[([^\]]+)\]\s*(.*)$/
const ENTRY_FIELD = /^-\s+([A-Za-z_][A-Za-z0-9_]*):\s?(.*)$/

/** Statuses that close the finding an entry `resolves:`. Mirrors verify_lib. */
const CLOSING: LogEntryStatus[] = ['resolved', 'wontfix', 'superseded']

/**
 * Parse a sidecar log. Tolerant on purpose — a human can open one of these in
 * Obsidian and append a comment by hand, and their entry has to survive the
 * round trip into the next agent sweep verbatim.
 */
export function parseVerificationLog(raw: string): VerificationLog {
  let attrs: Record<string, unknown> = {}
  let body = raw
  try {
    const parsed = fm<Record<string, unknown>>(raw)
    attrs = (parsed.attributes ?? {}) as Record<string, unknown>
    body = parsed.body
  } catch {
    body = raw.replace(/^---\n[\s\S]*?\n---\n?/, '')
  }

  const entries: LogEntry[] = []
  let current: LogEntry | null = null
  let lastField: string | null = null

  const setField = (key: string, value: string) => {
    if (!current) return
    const existing = current.fields.find((f) => f.key === key)
    if (existing) existing.value = value
    else current.fields.push({ key, value })
  }

  for (const line of body.split('\n')) {
    const heading = ENTRY_HEADING.exec(line)
    if (heading) {
      current = {
        id: heading[1].trim(),
        title: heading[2].trim(),
        entryType: '', author: '', date: '', severity: '', status: '', resolves: '',
        applied: false,
        fields: [],
      }
      entries.push(current)
      lastField = null
      continue
    }
    if (!current) continue
    const field = ENTRY_FIELD.exec(line)
    if (field) {
      lastField = field[1]
      setField(lastField, field[2].trim())
      continue
    }
    if (lastField && /^[ \t]/.test(line) && line.trim()) {
      // A wrapped value: `evidence:` prose routinely runs to several lines.
      const existing = current.fields.find((f) => f.key === lastField)
      if (existing) existing.value = `${existing.value} ${line.trim()}`.trim()
      continue
    }
    if (!line.trim()) lastField = null
  }

  for (const entry of entries) {
    const get = (key: string) => entry.fields.find((f) => f.key === key)?.value ?? ''
    entry.entryType = get('entry_type').toLowerCase() as LogEntryType | ''
    entry.author = get('author')
    entry.date = get('date')
    entry.severity = get('severity').toLowerCase() as LogEntrySeverity | ''
    entry.status = get('status').toLowerCase() as LogEntryStatus | ''
    entry.resolves = get('resolves')
    entry.applied = get('applied').toLowerCase() === 'true'
  }

  return {
    target: attrs.target == null ? '' : String(attrs.target),
    created: attrs.created instanceof Date
      ? attrs.created.toISOString().slice(0, 10)
      : String(attrs.created ?? ''),
    entries,
  }
}

/** Every entry id that something later in the log has closed. */
function closedEntryIds(log: VerificationLog): Set<string> {
  const closed = new Set<string>()
  for (const entry of log.entries) {
    const closes = CLOSING.includes(entry.status as LogEntryStatus)
    if (entry.resolves && closes) closed.add(entry.resolves)
    if (closes) closed.add(entry.id)
  }
  return closed
}

/**
 * Findings still open, accounting for later resolutions. Nothing in a log is
 * ever edited, so "is it still open" is always a question about what came after.
 */
export function openFindings(log: VerificationLog): LogEntry[] {
  const closed = closedEntryIds(log)
  return log.entries.filter((e) => e.entryType === 'finding' && !closed.has(e.id))
}

export function openCriticalFindings(log: VerificationLog): LogEntry[] {
  return openFindings(log).filter((e) => e.severity === 'critical')
}

// ─── Panel presentation ──────────────────────────────────────────────────────

export interface SourceSummary {
  /** The source's name — what a reader recognises it by. */
  label: string
  /** Where it can be read, when the citation names a URL. */
  url: string | null
  /** The chapters, sections and pages the claim was checked on. */
  locator: string | null
}

const SOURCE_URL = /https?:\/\/[^\s)>\]]+/
/** The auditor's fingerprint of the exact file that was read. */
const SOURCE_HASH = /\bsha-?256[:\s]+[0-9a-f]{16,}\b/gi
/** A parenthetical carrying that fingerprint, and nothing a reader wants. */
const HASH_PAREN = /\s*\([^()]*sha-?256[^()]*\)/gi

/**
 * Where in a citation the work stops being named and starts being located.
 *
 * A locator segment opens with a chapter, a section, a page or the word for one
 * — optionally behind an "and" / "incl." that continues a previous locator. Two
 * near misses are deliberately not here: `Q17` (a paper's question number reads
 * as part of what the source *is* — "CAS Exam 5 Fall 2019, Q17") and a bare
 * `PDF` (it opens plenty of titles), which arrives through `printed …` or
 * `PDF pp.` instead.
 */
const LOCATOR_START =
  /^(?:(?:and|incl\.?|including|plus|also|see|at)\s+)*(?:ch(?:apter)?s?\.?\s*\d|chapters?\b|§|sections?\b|pp?\.\s*[0-9A-Z]|pp?\s+\d|pages?\s+\d|printed\b|standard\s+pp?\b|appendi(?:x|ces)\b|principles?\b|tables?\s+\d|figs?\.|figures?\s+\d|exhibits?\b|footnotes?\b|fn\.|paras?\.|paragraphs?\b|arts?\.|articles?\s+\d|slides?\s+\d|PDF\s+pp?\.|domains?\s+[A-Z0-9]|parts?\s+[IVX0-9])/i

/** Punctuation left stranded by cutting a citation in two. */
function trimCitationEdges(text: string): string {
  return text.replace(/^[\s,;:—–-]+/, '').replace(/[\s,;:—–(-]+$/, '').trim()
}

/**
 * Where the locator begins, or -1 — the index of the separator to cut on.
 *
 * Two separators say it, and the earlier one wins. The convention is
 * `<name> — <where it was checked>`, but plenty of citations run the locator on
 * after a comma and only then reach a dash ("… (CAS, 5th ed. May 2016), Ch. 2
 * p.29 — NCCI loss costs …"), where cutting at the dash would keep the chapter
 * in the title. Commas and dashes inside brackets are part of whatever they sit
 * in, so the scan only sees the top level.
 */
function locatorCut(text: string): number {
  let depth = 0
  let segmentStart = 0
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (c === '(' || c === '[') depth++
    else if (c === ')' || c === ']') depth = Math.max(0, depth - 1)
    else if (depth > 0) continue
    else if (c === '—' || c === '–') return i
    else if (c === ',') {
      const segment = text.slice(segmentStart, i)
      if (segmentStart > 0 && LOCATOR_START.test(trimCitationEdges(segment))) return segmentStart - 1
      segmentStart = i + 1
    }
  }
  const last = text.slice(segmentStart)
  if (segmentStart > 0 && LOCATOR_START.test(trimCitationEdges(last))) return segmentStart - 1
  return -1
}

/**
 * Cut a cited source into the three parts worth printing.
 *
 * A citation is written for an auditor, not a reader: it carries the URL, a
 * sha256 of the exact file that was read, a version string and the pages the
 * claim was checked on. The hash and the version have to stay in the vault — it
 * is what makes the check reproducible — but on screen they bury the two things
 * a student wants, which are *which book* and *which pages*. So the name
 * becomes the card's title, the pages become the line under it, the URL becomes
 * the way to go and read it, and the fingerprint doesn't reach the screen at
 * all.
 */
export function summarizeSource(raw: string): SourceSummary {
  const text = (raw ?? '').trim()
  const match = SOURCE_URL.exec(text)
  const url = match ? match[0].replace(/[.,;:]+$/, '') : null

  // Everything that isn't the citation's prose, removed before it is cut — the
  // URL and the hash both sit *between* the name and the locator as often as
  // they sit after them.
  const prose = (match ? text.slice(0, match.index) + ' ' + text.slice(match.index + match[0].length) : text)
    .replace(HASH_PAREN, ' ')
    .replace(SOURCE_HASH, ' ')
    .replace(/\(\s*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const cut = locatorCut(prose)
  let label = trimCitationEdges(cut < 0 ? prose : prose.slice(0, cut))
  const locator = cut < 0 ? '' : trimCitationEdges(prose.slice(cut + 1))
  if (!label) label = url ?? text
  return { label, url, locator: locator || null }
}

export interface LogEntryGroup {
  entry: LogEntry
  /** The later entry that closed it, when one exists. */
  closedBy: LogEntry | null
}

/**
 * A page's log, split into the three things a reader is actually asking:
 * what is still wrong, what has been put right, and what people have said.
 */
export interface LogSummary {
  /** Open findings, worst first. */
  open: LogEntryGroup[]
  /** Findings something later closed, plus standalone corrections. */
  resolved: LogEntryGroup[]
  /** Comments and questions — reader reports, mostly. */
  notes: LogEntryGroup[]
  /**
   * The checks themselves — the entries that set the page's status (a
   * validation pass), newest first: *what was checked, when*.
   */
  checks: LogEntryGroup[]
}

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0, major: 1, minor: 2, nit: 3,
}

export function summarizeLog(log: VerificationLog): LogSummary {
  const closed = closedEntryIds(log)
  const closers = new Map<string, LogEntry>()
  for (const entry of log.entries) {
    if (entry.resolves && CLOSING.includes(entry.status as LogEntryStatus)) {
      closers.set(entry.resolves, entry)
    }
  }

  const summary: LogSummary = { open: [], resolved: [], notes: [], checks: [] }
  for (const entry of log.entries) {
    // A resolution belongs to the finding it closes; listing it separately would
    // say the same thing twice, in two places, out of order.
    if (entry.resolves && closers.get(entry.resolves) === entry) continue
    const group: LogEntryGroup = { entry, closedBy: closers.get(entry.id) ?? null }
    if (entry.entryType === 'finding') {
      if (closed.has(entry.id)) summary.resolved.push(group)
      else summary.open.push(group)
    } else if (entry.entryType === 'correction' || entry.entryType === 'resolution') {
      summary.resolved.push(group)
    } else if (logField(entry, 'status_set')) {
      summary.checks.unshift(group)
    } else {
      summary.notes.push(group)
    }
  }

  summary.open.sort((a, b) =>
    (SEVERITY_ORDER[a.entry.severity] ?? 9) - (SEVERITY_ORDER[b.entry.severity] ?? 9))
  return summary
}

/** One `- key: value` field of an entry, or '' when it has none. */
export function logField(entry: LogEntry, key: string): string {
  return entry.fields.find((f) => f.key === key)?.value ?? ''
}

export interface EvidenceSummary {
  /** The evidence as prose, with the auditor's fingerprints taken out. */
  text: string
  /** The links it cited, in order, so they can be offered as links. */
  links: string[]
}

/**
 * A fingerprint as evidence prose writes it — in full, or cut short behind an
 * ellipsis ("sha256:1cb44e7f..."), which a source line never is.
 */
const EVIDENCE_HASH = /\bsha-?256[:\s]+[0-9a-f]{6,}(?:\.{3}|…)?/gi

/** Separators a removed URL or hash can leave stranded. Not `-`: it is a minus sign too. */
const STRANDED_CHARS = ',;:—–'
const STRANDED = `[${STRANDED_CHARS}]`

/**
 * Evidence as a reader can read it.
 *
 * An `evidence:` line is where a finding says what the source says, and it is
 * the other half of the diff the panel draws against what the page said. Most
 * of it is plain prose, but a citation inside it carries what `summarizeSource`
 * cuts off a source line — a sha256 of the file that was read, and the URL it
 * was read at — usually in the middle of a sentence ("… REFERENCES pp.5-7,
 * sha256:bed2…, https://…. p.5 lists …"). Both come out, the URL to be offered
 * as a link instead, and the separators they leave behind are tidied. Text with
 * neither is returned exactly as written: the tidying is only ever a repair of
 * what the cut left, never an edit of the finding.
 */
export function summarizeEvidence(raw: string): EvidenceSummary {
  const text = (raw ?? '').trim()
  const links = [...text.matchAll(new RegExp(SOURCE_URL.source, 'g'))]
    .map((m) => m[0].replace(/[.,;:]+$/, ''))
  const hasHash = new RegExp(EVIDENCE_HASH.source, 'i').test(text)
  if (links.length === 0 && !hasHash) return { text, links }

  // The hash alone, not the bracket around it as `summarizeSource` drops: in
  // evidence that bracket is as often the name of what was read ("P-21-05
  // (Risk and Insurance, sha256:1cb4…)") as the auditor's aside.
  let prose = links
    .reduce((t, url) => t.split(url).join(' '), text)
    .replace(EVIDENCE_HASH, ' ')
    .replace(/\s+/g, ' ')

  let previous = ''
  while (prose !== previous) {
    previous = prose
    prose = prose
      // Brackets the cut emptied, or left opening or closing on a separator.
      .replace(new RegExp(`\\(\\s*(?:${STRANDED}\\s*)*\\)`, 'g'), '')
      .replace(new RegExp(`\\(\\s*${STRANDED}\\s*`, 'g'), '(')
      .replace(new RegExp(`\\s*${STRANDED}\\s*\\)`, 'g'), ')')
      // Two separators in a row where something between them was taken out.
      .replace(new RegExp(`(${STRANDED})(?:\\s*${STRANDED})+`, 'g'), '$1')
      .replace(new RegExp(`${STRANDED}\\s*\\.`, 'g'), '.')
      .replace(/\s+([,;:.)])/g, '$1')
      .replace(/\s+/g, ' ')
  }
  // Not `trimCitationEdges`: evidence can open on a negative number.
  const trimmed = prose
    .replace(new RegExp(`^[\\s${STRANDED_CHARS}]+`), '')
    .replace(new RegExp(`[\\s(${STRANDED_CHARS}]+$`), '')
  return { text: trimmed, links: [...new Set(links)] }
}

/**
 * What became of a finding — the line under the diff.
 *
 * `fixed` / `wontfix` / `superseded` are the three ways something later in the
 * log (or the finding's own status) closes it, with the date it was closed and
 * what the closing entry said. `applied` is an open finding whose correction
 * has already been made to the page; `proposed` is one still waiting for it.
 */
export type FindingOutcome =
  | { kind: 'fixed' | 'wontfix' | 'superseded'; date: string; note: string }
  | { kind: 'applied' | 'proposed'; note: string }

export function findingOutcome(group: LogEntryGroup): FindingOutcome | null {
  const { entry, closedBy } = group
  if (entry.entryType !== 'finding') return null
  const proposed = logField(entry, 'proposed_action')
  const status = closedBy?.status || entry.status
  if (CLOSING.includes(status as LogEntryStatus)) {
    const said = closedBy ? logField(closedBy, 'note') : ''
    // A finding closed without a word is fixed the way it proposed. One set
    // aside is not: its proposal is exactly what was *not* done.
    if (status === 'resolved') return { kind: 'fixed', date: closedBy?.date ?? '', note: said || proposed }
    return { kind: status as 'wontfix' | 'superseded', date: closedBy?.date ?? '', note: said }
  }
  if (entry.applied) return { kind: 'applied', note: proposed }
  return proposed ? { kind: 'proposed', note: proposed } : null
}


/**
 * Should this page be kept out of a quiz session by default?
 *
 * True when something critical is on file about it: an unresolved critical
 * finding, or a `disputed` status (which means either sources conflict or a
 * critical finding is unresolved). Serving a student a question that the record
 * says is wrong is the exact failure this whole layer exists to prevent — but it
 * is a *default*, not a lock, because reviewing flagged questions is how they get
 * fixed. `hooks/useShowFlaggedQuestions.ts` is the toggle.
 */
export function hasCriticalFinding(v: Verification | null | undefined): boolean {
  if (!v) return false
  return v.openCritical > 0 || v.status === 'disputed'
}
